import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/styled/Button';
import Input from '../components/styled/Input';
import Card from '../components/styled/Card';
import Tooltip from '../components/styled/Tooltip';

const BaseFormTemplate = ({ 
  title, 
  description, 
  fields, 
  onSubmit,
  onCancel,
  initialData = {},
  submitButtonText = null,
  cancelButtonText = null
}) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (name, value, type = 'text') => {
    let processedValue = value;

    // For certain data types, we may want to process them differently
    if (type === 'number') {
      processedValue = value === '' ? '' : Number(value);
    } else if (type === 'date') {
      // Keep as string for date inputs
    } else if (type === 'boolean') {
      processedValue = value === true || value === 'true';
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    for (const field of fields) {
      if (field.required && (!formData[field.name] || formData[field.name] === '')) {
        newErrors[field.name] = t('form.validation.required', { field: field.label || field.name });
      } else if (field.validation && formData[field.name]) {
        const validationError = field.validation(formData[field.name]);
        if (validationError) {
          newErrors[field.name] = validationError;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    const isRequired = field.required;

    // Function to render label with tooltip if tooltip content exists
    const renderLabelWithTooltip = (label, tooltipContent) => {
      if (!tooltipContent) {
        return `${label} ${isRequired ? '*' : ''}`;
      }

      return (
        <div className="flex items-center gap-2">
          <span>{`${label} ${isRequired ? '*' : ''}`}</span>
          <Tooltip 
            content={tooltipContent}
            position="top"
          >
            <span className="cursor-help text-gray-500 hover:text-gray-700">
              ℹ️
            </span>
          </Tooltip>
        </div>
      );
    };

    // Get localized tooltip content
    let tooltipContent = null;
    if (field.tooltip) {
      if (typeof field.tooltip === 'string') {
        tooltipContent = field.tooltip;
      } else if (field.tooltip[i18n.language]) {
        tooltipContent = field.tooltip[i18n.language];
      } else if (field.tooltip.en) {
        tooltipContent = field.tooltip.en;
      }
    }

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'number':
        return (
          <Input
            key={field.name}
            label={renderLabelWithTooltip(field.label, tooltipContent)}
            name={field.name}
            type={field.type}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value, field.type)}
            error={error}
            placeholder={field.placeholder || ''}
            fullWidth={field.fullWidth !== false}
            lang={i18n.language}
          />
        );
      
      case 'textarea':
        return (
          <Input
            key={field.name}
            label={renderLabelWithTooltip(field.label, tooltipContent)}
            name={field.name}
            type="textarea"
            rows={field.rows || 4}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            error={error}
            placeholder={field.placeholder || ''}
            fullWidth={field.fullWidth !== false}
            lang={i18n.language}
          />
        );
      
      case 'select':
        return (
          <Input
            key={field.name}
            label={renderLabelWithTooltip(field.label, tooltipContent)}
            name={field.name}
            type="select"
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            error={error}
            options={field.options || []}
            fullWidth={field.fullWidth !== false}
            lang={i18n.language}
          />
        );
      
      case 'date':
        return (
          <Input
            key={field.name}
            label={renderLabelWithTooltip(field.label, tooltipContent)}
            name={field.name}
            type="date"
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            error={error}
            fullWidth={field.fullWidth !== false}
            lang={i18n.language}
          />
        );
      
      case 'checkbox':
        return (
          <div key={field.name} className="flex items-center">
            <input
              type="checkbox"
              id={field.name}
              checked={!!value}
              onChange={(e) => handleChange(field.name, e.target.checked, 'boolean')}
              className={`h-4 w-4 rounded border-gray-300 text-hemp-green-600 focus:ring-hemp-green-500 ${error ? 'border-red-500' : ''}`}
            />
            <label htmlFor={field.name} className="ml-2 block text-sm text-gray-900">
              {renderLabelWithTooltip(field.label, tooltipContent)}
            </label>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
      
      case 'radio':
        return (
          <div key={field.name} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {renderLabelWithTooltip(field.label, tooltipContent)}
            </label>
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`${field.name}-${option.value}`}
                  name={field.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  className="h-4 w-4 border-gray-300 text-hemp-green-600 focus:ring-hemp-green-500"
                />
                <label htmlFor={`${field.name}-${option.value}`} className="ml-2 block text-sm text-gray-700">
                  {option.label || option.value}
                </label>
              </div>
            ))}
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
      
      default:
        return (
          <Input
            key={field.name}
            label={renderLabelWithTooltip(field.label, tooltipContent)}
            name={field.name}
            type={field.type || 'text'}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            error={error}
            placeholder={field.placeholder || ''}
            fullWidth={field.fullWidth !== false}
            lang={i18n.language}
          />
        );
    }
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
        {description && <p className="text-gray-600">{description}</p>}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {fields.map(field => renderField(field))}

        <div className="flex space-x-4 pt-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={loading}
          >
            {submitButtonText || t('form.submit') || 'Submit'}
          </Button>
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onCancel}
              disabled={loading}
            >
              {cancelButtonText || t('form.cancel') || 'Cancel'}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
};

export default BaseFormTemplate;